if (!customElements.get('tcl-product-tabs-controller')) {
  customElements.define('tcl-product-tabs-controller', class extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    initialize() {
      this.frame = null;
      this.root = this.closest('[data-tcl-product-tabs]');
      if (!this.root || this.active) return;
      this.active = true;
      this.cleanups = [];
      this.nav = this.root.querySelector('.wbs-pck__tab-button_top');
      this.anchor = this.root.querySelector('[data-tcl-tab-anchor]');
      // Global [aria-hidden] styles hide the empty anchor unless it is restored to layout.
      if (this.anchor) this.anchor.style.display = 'block';
      this.product = Array.from(document.querySelectorAll('product-info')).find(
        (element) => element.dataset.productId === this.dataset.productId && element.dataset.updateUrl === 'true'
      );
      this.sticky = this.product?.querySelector('.sticky-container_box');
      this.listen(this.root, 'click', (event) => {
        const tab = event.target.closest('[data-wbs-pck-tab]');
        if (tab && this.root.contains(tab)) this.selectTab(tab.dataset.wbsPckTab, true);
      });
      this.listen(document, 'shopify:block:select', (event) => {
        if (!this.root.contains(event.target)) return;
        const panel = event.target.closest('[data-wbs-pck-panel]');
        const id = panel?.dataset.wbsPckPanel || event.detail?.blockId;
        this.selectTab(id, false);
      });
      this.listen(window, 'scroll', () => this.schedule(), { passive: true });
      this.listen(window, 'resize', () => this.schedule());
      this.listen(window, 'load', () => this.schedule());
      if (this.sticky) {
        this.mutation = new MutationObserver(() => this.schedule());
        this.mutation.observe(this.sticky, { attributes: true, attributeFilter: ['class'] });
      }
      if (typeof ResizeObserver !== 'undefined') {
        this.resize = new ResizeObserver(() => this.schedule());
        this.resize.observe(this.root);
        if (this.sticky) this.resize.observe(this.sticky);
      }
      if (this.dataset.animation !== 'none' && typeof IntersectionObserver !== 'undefined') {
        this.intersection = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            this.root.classList.add('wbs-pck__animation-applied');
          }
        }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
        this.intersection.observe(this.root);
      }
      const subscribeToVariant = () => {
        if (this.unsubscribe || typeof subscribe !== 'function') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!this.product || ![this.product.dataset.section, this.product.dataset.originalSection].includes(data.sectionId)) return;
          this.showVariant(data.variant?.id);
        });
      };
      subscribeToVariant();
      if (document.readyState === 'loading') this.listen(document, 'DOMContentLoaded', subscribeToVariant, { once: true });
      const selected = this.product?.querySelector('variant-selects [data-selected-variant]');
      if (selected) {
        try { this.showVariant(JSON.parse(selected.textContent)?.id); } catch (_) { /* Keep server-rendered state. */ }
      }
      this.updateSticky();
    }

    listen(target, type, callback, options) {
      target.addEventListener(type, callback, options);
      this.cleanups.push(() => target.removeEventListener(type, callback, options));
    }

    schedule() {
      if (this.frame != null || !this.active) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.active) this.updateSticky();
      });
    }

    updateSticky() {
      if (!this.nav || !this.anchor) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const height = !mobile && this.sticky?.classList.contains('is-visible') ? this.sticky.offsetHeight : 0;
      const sticky = this.anchor.getBoundingClientRect().top <= height;
      this.nav.classList.toggle('is-sticky', sticky);
      this.nav.style.top = sticky ? `${height}px` : '';
      this.anchor.style.height = sticky ? `${this.nav.offsetHeight}px` : '';
    }

    selectTab(id, scroll) {
      const panels = Array.from(this.root.querySelectorAll('[data-wbs-pck-panel]'));
      const panel = panels.find((element) => element.dataset.wbsPckPanel === id);
      if (!panel) return;
      this.root.querySelectorAll('[data-wbs-pck-tab]').forEach((tab) => {
        tab.setAttribute('aria-selected', String(tab.dataset.wbsPckTab === id));
      });
      panels.forEach((element) => element.setAttribute('aria-hidden', String(element !== panel)));
      clearTimeout(this.scrollTimer);
      if (scroll) this.scrollTimer = setTimeout(() => {
        if (!this.active) return;
        const offset = (this.nav?.classList.contains('is-sticky') ? this.nav.offsetHeight : 0) +
          (this.sticky?.classList.contains('is-visible') ? this.sticky.offsetHeight : 0);
        window.scrollTo({ top: panel.getBoundingClientRect().top + window.pageYOffset - offset, behavior: 'smooth' });
      }, 100);
      this.schedule();
    }

    showVariant(id) {
      this.root.querySelectorAll('[data-tcl-variant-panels]').forEach((group) => {
        let found = false;
        group.querySelectorAll('[data-variant-id]').forEach((panel) => {
          const match = !!id && panel.dataset.variantId === String(id);
          panel.style.display = match ? '' : 'none';
          found ||= match;
        });
        group.style.display = found ? '' : 'none';
      });
      this.schedule();
    }

    disconnectedCallback() {
      this.active = false;
      if (this.frame != null) cancelAnimationFrame(this.frame);
      this.frame = null;
      clearTimeout(this.scrollTimer);
      this.cleanups?.forEach((cleanup) => cleanup());
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.mutation?.disconnect();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.nav?.classList.remove('is-sticky');
      if (this.nav) this.nav.style.top = '';
      if (this.anchor) {
        this.anchor.style.height = '';
        this.anchor.style.display = '';
      }
    }
  });
}
